import { useState, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import Navbar from '../components/common/NavBar';
import MiniHero from '../components/common/MiniHero';
import Footer from '../components/common/Footer';
import { get } from '../utils/api';
import { getUserLanguage } from '../utils/language';
import { IMAGE_BASE_URL } from '../config';
import { useCart } from '@context/CartContext';
import { Link } from 'react-router-dom';

const Shop = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const language = getUserLanguage();
  const { addToCart } = useCart();

  const { ref: sectionRef, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const headers = { 'accept-language': language };
        const data = await get('/products', headers);
        setProducts(data);
      } catch (error) {
        console.error('Error fetching products:', error);
        setError(language === 'cs' ? 'Nepodařilo se načíst produkty' : 'Failed to load products data');
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [language]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black">
        <Navbar language={language} />
        <MiniHero title={language === 'cs' ? 'E-shop' : 'Shop'} />
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red"></div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black">
        <Navbar language={language} />
        <MiniHero title={language === 'cs' ? 'E-shop' : 'Shop'} />
        <div className="text-white text-center py-20">{error}</div>
        <Footer />
      </div>
    );
  }

  const isBestseller = (product) => product.is_bestseller === 1 || product.is_bestseller === true;
  const isNew = (product) => product.is_new === 1 || product.is_new === true;

  return (
    <div className="min-h-screen bg-black">
      <Navbar language={language} />
      <MiniHero title={language === 'cs' ? 'E-shop' : 'Shop'} />
      <section className="py-20 bg-dark-gray relative" ref={sectionRef}>
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((product, index) => {
              const productData = product.translations && product.translations[language]
                ? product.translations[language]
                : (product.translations && product.translations.en
                  ? product.translations.en
                  : { name: '', description: '' });
              const mainPhoto = product.photos && product.photos.length > 0 
                ? product.photos[0].image_path 
                : product.image_path;

              return (
                <div
                  key={product.id}
                  className={`drift-card group ${inView ? 'animate-fade-in' : 'opacity-0'}`}
                  style={{ animationDelay: `${0.2 * index}s` }}
                >
                  <Link to={`/product/${product.slug}`} className="block">
                    <div className="relative overflow-hidden h-60">
                      {isBestseller(product) && (
                        <div className="absolute top-2 right-2 bg-red-600 text-white px-3 py-1 z-10 rounded-full text-xs font-semibold">
                          {language === 'cs' ? 'Bestseller' : 'Bestseller'}
                        </div>
                      )}
                      {isNew(product) && (
                        <div className="absolute top-2 left-2 bg-green-600 text-white px-3 py-1 z-10 rounded-full text-xs font-semibold">
                          {language === 'cs' ? 'Novinka' : 'New'}
                        </div>
                      )}
                      <img
                        src={mainPhoto ? `${IMAGE_BASE_URL}${mainPhoto}` : '/images/products/placeholder.jpg'}
                        alt={productData.name || (language === 'cs' ? 'Produkt' : 'Product')}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = '/images/products/placeholder.jpg';
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent"></div>
                    </div>
                  </Link>
                  <div className="p-6">
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="text-xl font-title font-bold text-white group-hover:text-red-500 transition-colors">
                        {productData.name}
                      </h3>
                      <span className="price-tag">
                        {new Intl.NumberFormat(language === 'cs' ? 'cs-CZ' : 'en-US').format(product.price)}{' '}
                        <span className="currency">{language === 'cs' ? 'Kč' : 'CZK'}</span>
                      </span>
                    </div>
                    <div className="gradient-line mb-4"></div>
                    <p className="text-gray-400 text-sm mb-4 h-16 overflow-hidden">{productData.description}</p>
                    {product.features && product.features.length > 0 && (
                      <ul className="features-list text-gray-400 text-sm mb-6">
                        {product.features.slice(0, 3).map((feature, idx) => (
                          <li key={idx}>{feature[language] || feature.en}</li>
                        ))}
                      </ul>
                    )}
                    <button
                      onClick={() => addToCart(product)}
                      className="btn-drift w-full flex justify-center"
                    >
                      <i className="fas fa-shopping-cart text-xs mr-2"></i>
                      {language === 'cs' ? 'Přidat do košíku' : 'Add to Cart'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default Shop;